import { useEffect, useState } from "react";
import api from "./api";
import { SOURCES, STATUSES, labelFor } from "./constants";

// Module-level cache so every component shares the same fetch + result.
let cache = null;
let inflight = null;
const subscribers = new Set();

function notify() {
  subscribers.forEach((cb) => cb(cache));
}

async function fetchOptions() {
  if (inflight) return inflight;
  inflight = api
    .get("/meta/lead-options")
    .then(({ data }) => {
      const knownStatusIds = new Set(STATUSES.map((s) => s.id));
      const knownSourceIds = new Set(SOURCES.map((s) => s.id));

      // Title-case helper for unknown ids ("follow_up" → "Follow Up")
      const humanize = (id) =>
        (id || "")
          .toString()
          .replace(/[_-]+/g, " ")
          .replace(/\s+/g, " ")
          .trim()
          .split(" ")
          .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
          .join(" ");

      const mergedStatuses = (data.statuses || []).map((id) => {
        const known = STATUSES.find((s) => s.id === id);
        if (known) return known;
        return { id, label: humanize(id), color: "#52525B", custom: true };
      });

      const mergedSources = (data.sources || []).map((id) => {
        const known = SOURCES.find((s) => s.id === id);
        if (known) return known;
        return { id, label: humanize(id), short: humanize(id).slice(0, 6).toUpperCase(), custom: true };
      });

      cache = { statuses: mergedStatuses, sources: mergedSources };
      notify();
      return cache;
    })
    .catch(() => {
      cache = { statuses: STATUSES, sources: SOURCES };
      notify();
      return cache;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function refreshLeadOptions() {
  cache = null;
  return fetchOptions();
}

export function useLeadOptions() {
  const [opts, setOpts] = useState(cache || { statuses: STATUSES, sources: SOURCES });
  useEffect(() => {
    subscribers.add(setOpts);
    if (!cache) fetchOptions();
    return () => subscribers.delete(setOpts);
  }, []);
  return opts;
}

export { labelFor };
